/**
 * DataSourceContext - Manages data source connections and fetching
 *
 * Sprint 11a: Wires data packages to consumer app
 * v13 Section 5 - Data Source Sync Architecture
 *
 * PACKAGES USED:
 * - @ownyou/email: EmailPipeline for email sync
 * - @ownyou/iab-classifier: createIABClassifier for classification
 * - @ownyou/data-calendar: Calendar event fetch → classify → store pipeline (Sprint 15)
 * - @ownyou/data-financial: Transaction fetch → classify → store pipeline (Sprint 15)
 *
 * Responsibilities:
 * 1. Track connected data sources (email, calendar, financial)
 * 2. Store OAuth tokens in secure storage
 * 3. Trigger data fetching when sources connect
 * 4. Run IAB classification on fetched data
 * 5. Store classified data in LangGraph Store
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { NS } from '@ownyou/shared-types';
import { useStore } from './StoreContext';
import { useAuth } from './AuthContext';
import { refreshOAuthToken, type OAuthTokenData } from '../utils/tauri-oauth';
import { getPlatform } from '../utils/platform';
import {
  getMicrosoftAccessToken,
  getGoogleAccessToken,
  getTokenManager,
  ReAuthRequiredError,
} from '../lib/oauth-integration';
import { setDisclosurePreference } from '../services/extension-credential-sync';
import { useModelPreferences } from '../hooks/useModelPreferences';
import {
  executeLlmTextRoute,
  resolveIabClassificationRoutePlan,
  type ModelRouteId,
} from '../lib/llm-routing';
import {
  createMissionSpineRunId,
  recordMissionSpineMarker,
} from '../lib/mission-spine/instrumentation';
import {
  writeConnectedSourceAuthOutcome,
  writeDisconnectedSourceAuthOutcome,
  writeNeedsReauthSourceAuthOutcome,
} from '../lib/source-read-model/read-model';

// Types for data source management
export type DataSourceId = 'gmail' | 'outlook' | 'google-calendar' | 'microsoft-calendar' | 'plaid' | 'chrome-history';
export type DataSourceProvider = 'google' | 'microsoft' | 'plaid' | 'browser-extension';
export type DataSourceType = 'email' | 'calendar' | 'financial' | 'browsing';
export type DataSourceStatus = 'disconnected' | 'connecting' | 'connected' | 'error' | 'syncing';

export interface DataSource {
  id: DataSourceId;
  provider: DataSourceProvider;
  type: DataSourceType;
  status: DataSourceStatus;
  lastSync?: Date;
  itemCount?: number;
  error?: string;
}

export interface SyncResult {
  success: boolean;
  itemCount: number;
  error?: string;
}

/** Token data stored in secure storage */
interface StoredTokenData {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number; // Unix timestamp
  provider: 'google' | 'microsoft' | 'plaid';
  savedAt: number;
}

/** Sync metadata stored separately from tokens (Bug 1 fix) */
interface StoredSyncMetadata {
  itemCount: number;
  lastSync: string; // ISO date string
}

function missionSpineNamespaceForSource(type: DataSourceType): string {
  switch (type) {
    case 'email':
      return 'ownyou.iab';
    case 'calendar':
      return 'ownyou.calendar_events';
    case 'financial':
      return 'ownyou.financial_transactions';
    case 'browsing':
      return 'ownyou.browsing_history';
  }
}

interface DataSourceContextValue {
  /** All available data sources with their connection status */
  dataSources: DataSource[];

  /** Connect a data source with OAuth token data */
  connectSource: (sourceId: DataSourceId, tokenData: OAuthTokenData) => Promise<void>;

  /** Disconnect a data source */
  disconnectSource: (sourceId: DataSourceId) => Promise<void>;

  /** Sync a specific data source (fetch + classify + store) */
  syncSource: (sourceId: DataSourceId) => Promise<SyncResult>;

  /** Sync all connected data sources */
  syncAll: () => Promise<void>;

  /** Whether any source is currently syncing */
  isSyncing: boolean;

  /** Get connected sources */
  getConnectedSources: () => DataSource[];

  /** Check if a specific source is connected */
  isSourceConnected: (sourceId: DataSourceId) => boolean;

  /** Get a valid access token for a source, refreshing if expired or forced */
  getValidToken: (sourceId: DataSourceId, forceRefresh?: boolean) => Promise<string | null>;

  /** Attempt to refresh expired tokens for disconnected sources. Returns IDs that could not be refreshed. */
  refreshExpiredTokens: () => Promise<DataSourceId[]>;
}

const DataSourceContext = createContext<DataSourceContextValue | null>(null);

const INITIAL_DATA_SOURCES: DataSource[] = [
  { id: 'gmail', provider: 'google', type: 'email', status: 'disconnected' },
  { id: 'outlook', provider: 'microsoft', type: 'email', status: 'disconnected' },
  { id: 'google-calendar', provider: 'google', type: 'calendar', status: 'disconnected' },
  { id: 'microsoft-calendar', provider: 'microsoft', type: 'calendar', status: 'disconnected' },
  { id: 'plaid', provider: 'plaid', type: 'financial', status: 'disconnected' },
  { id: 'chrome-history', provider: 'browser-extension', type: 'browsing', status: 'disconnected' },
];

interface DataSourceProviderProps {
  children: ReactNode;
}

export function DataSourceProvider({ children }: DataSourceProviderProps) {
  const { store, isReady } = useStore();
  const { wallet, isAuthenticated } = useAuth();
  const { getAccessToken } = usePrivy();
  const { routeSelections, models: availableModels } = useModelPreferences();
  const userId = wallet?.address ?? 'anonymous';

  const [dataSources, setDataSources] = useState<DataSource[]>(INITIAL_DATA_SOURCES);
  const [isSyncing, setIsSyncing] = useState(false);

  // Keep a ref to the latest dataSources so async callbacks avoid stale closures
  const dataSourcesRef = useRef(dataSources);
  useEffect(() => { dataSourcesRef.current = dataSources; }, [dataSources]);

  const buildIabRoutePlan = useCallback((routeProxyToken?: string | null) => {
    const plan = resolveIabClassificationRoutePlan({
      routeSelections,
      models: availableModels,
      authToken: routeProxyToken,
    });

    if (!plan.ok) {
      console.warn('[DataSourceContext] IAB classification deferred: unavailable route plan', {
        unavailable: plan.unavailable,
      });
      return null;
    }

    return plan;
  }, [routeSelections, availableModels]);

  // Load saved tokens on mount
  useEffect(() => {
    if (!isReady || !isAuthenticated || !store) return;
    loadSavedTokens();
  }, [isReady, isAuthenticated, store, userId]);

  // Auto-detect browser extension and connect browsing source
  // Runs AFTER loadSavedTokens via a longer delay (3s > loadSavedTokens async time)
  useEffect(() => {
    if (!isReady || !isAuthenticated || !store) return;

    let cancelled = false;

    const detectExtension = async () => {
      if (cancelled) return;
      try {
        const browsingApi = (window as any).OwnYouBrowsing;
        if (!browsingApi || !browsingApi.isAvailable()) return;

        const hasPermission = await browsingApi.hasPermission();
        if (hasPermission) {
          console.log('[DataSourceContext] Browser extension detected with history permission');

          // Check latest state via ref (not closure)
          const current = dataSourcesRef.current.find(ds => ds.id === 'chrome-history');
          if (current?.status === 'connected' || current?.status === 'syncing') {
            console.log('[DataSourceContext] Browsing already connected, skipping auto-detect');
            return;
          }

          // Store a marker so loadSavedTokens can restore it on next page load
          await store.put(NS.uiPreferences(userId), 'oauth_chrome-history', {
            accessToken: 'extension-local',
            provider: 'browser-extension',
            savedAt: Date.now(),
          });

          setDataSources(prev => prev.map(ds =>
            ds.id === 'chrome-history' ? { ...ds, status: 'connected' as DataSourceStatus } : ds
          ));

          // Sync browsing data — use short delay to ensure React has flushed the state
          if (cancelled) return;
          await new Promise(resolve => setTimeout(resolve, 200));
          if (cancelled) return;

          console.log('[DataSourceContext] Auto-syncing browsing history...');
          try {
            const result = await syncBrowsingDirect();
            console.log('[DataSourceContext] Browsing auto-sync result:', result);
          } catch (err) {
            console.warn('[DataSourceContext] Browsing auto-sync failed:', err);
          }
        }
      } catch (err) {
        console.warn('[DataSourceContext] Extension detection failed:', err);
      }
    };

    // Delay to allow bridge script injection + loadSavedTokens to complete
    const timer = setTimeout(detectExtension, 3000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isReady, isAuthenticated, store, userId]);

  /**
   * Direct browsing sync — bypasses syncSource to avoid stale closure issues.
   * Called only from the auto-detect effect above.
   */
  const syncBrowsingDirect = async (): Promise<SyncResult> => {
    if (!store) return { success: false, itemCount: 0, error: 'Store not ready' };

    setDataSources(prev => prev.map(ds =>
      ds.id === 'chrome-history' ? { ...ds, status: 'syncing' as DataSourceStatus } : ds
    ));

    try {
      const browsingApi = (window as any).OwnYouBrowsing;
      if (!browsingApi || !browsingApi.isAvailable()) {
        throw new Error('OwnYou browser extension not detected.');
      }

      const hasPermission = await browsingApi.hasPermission();
      if (!hasPermission) {
        throw new Error('Browsing history permission not granted.');
      }

      const result = await browsingApi.getHistory({ days: 30, maxResults: 500 });
      const entries = result.entries || [];
      console.log(`[DataSourceContext] [direct] Fetched ${entries.length} browsing entries`);

      if (entries.length > 0) {
        const browsingNs = NS.browsingHistory(userId);
        for (const entry of entries) {
          await store.put(browsingNs, `browse_${entry.hash || entry.domain + '_' + Date.now()}`, {
            url: entry.url,
            title: entry.title,
            domain: entry.domain,
            visitCount: entry.visitCount || 1,
            duration: entry.duration || 0,
            lastVisitTime: entry.lastVisitTime,
            transition: entry.transition || 'unknown',
            sourceType: 'browsing',
          });
        }
        console.log(`[DataSourceContext] [direct] Stored ${entries.length} browsing entries`);
      }

      const syncTime = new Date();
      setDataSources(prev => prev.map(ds =>
        ds.id === 'chrome-history' ? {
          ...ds,
          status: 'connected' as DataSourceStatus,
          lastSync: syncTime,
          itemCount: entries.length,
          error: undefined,
        } : ds
      ));

      // Persist sync metadata
      await store.put(NS.uiPreferences(userId), 'sync_chrome-history', {
        itemCount: entries.length,
        lastSync: syncTime.toISOString(),
      });

      // Notify pipelines
      window.dispatchEvent(new CustomEvent('ownyou:data-changed', { detail: { namespace: 'iab_classifications', sourceId: 'chrome-history' } }));
      window.dispatchEvent(new CustomEvent('data-changed', { detail: { source: 'browsing' } }));

      return { success: true, itemCount: entries.length };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Browsing sync failed';
      setDataSources(prev => prev.map(ds =>
        ds.id === 'chrome-history' ? { ...ds, status: 'error' as DataSourceStatus, error: errorMessage } : ds
      ));
      return { success: false, itemCount: 0, error: errorMessage };
    }
  };

  /**
   * Load OAuth tokens from secure storage and restore connection states
   *
   * BUG 1 FIX: Now also loads sync metadata (itemCount, lastSync) from Store
   * and preserves existing state instead of mapping over INITIAL_DATA_SOURCES
   */
  const loadSavedTokens = async () => {
    if (!store) return;

    try {
      // Check for saved tokens for each source
      // BUG 1 FIX: Use functional update to preserve existing state
      const updatedSources = await Promise.all(
        INITIAL_DATA_SOURCES.map(async (initialSource) => {
          try {
            // Helper: load sync metadata from LangGraph Store
            const loadSyncMeta = async () => {
              let itemCount: number | undefined;
              let lastSync: Date | undefined;
              try {
                const syncMeta = await store.get<StoredSyncMetadata>(
                  NS.uiPreferences(userId),
                  `sync_${initialSource.id}`
                );
                if (syncMeta) {
                  itemCount = syncMeta.itemCount;
                  lastSync = syncMeta.lastSync ? new Date(syncMeta.lastSync) : undefined;
                }
              } catch { /* no metadata yet */ }
              return { itemCount, lastSync };
            };

            // PWA + google/microsoft: BrowserTokenStorage is canonical
            // Validate token by attempting refresh — if refresh token is expired,
            // leave as disconnected so handleRefreshClick can trigger OAuth reconnect.
            if (getPlatform() === 'pwa' && (initialSource.provider === 'google' || initialSource.provider === 'microsoft')) {
              const providerKey = initialSource.provider as 'google' | 'microsoft';
              try {
                const tokenManager = getTokenManager();
                const tokens = await tokenManager.getTokens('default_user', providerKey);

                if (tokens?.refreshToken) {
                  // Actually validate by calling the access token getter (handles refresh internally)
                  try {
                    if (providerKey === 'google') {
                      await getGoogleAccessToken();
                    } else {
                      await getMicrosoftAccessToken();
                    }
                    // Token is valid (or was successfully refreshed)
                    await writeConnectedSourceAuthOutcome({
                      store,
                      userId,
                      sourceId: initialSource.id,
                    });
                    const { itemCount, lastSync } = await loadSyncMeta();
                    return {
                      ...initialSource,
                      status: 'connected' as DataSourceStatus,
                      itemCount,
                      lastSync,
                    };
                  } catch (validationErr) {
                    if (validationErr instanceof ReAuthRequiredError) {
                      // Refresh token expired — leave disconnected so reconnect flow can fire
                      await writeNeedsReauthSourceAuthOutcome({
                        store,
                        userId,
                        sourceId: initialSource.id,
                        reason: 'refresh_expired',
                      });
                      const { itemCount, lastSync } = await loadSyncMeta();
                      return { ...initialSource, itemCount, lastSync };
                    }
                    // RC1 (2026-05-19): strict-by-default.
                    // Non-auth validation failures used to optimistically flip the source to
                    // 'connected'. That caused false-positive Connected → Sync → Connect cycles
                    // when the token was junk but the failure looked like a transient network
                    // error. Surface the failure as 'error' instead — the user can retry, and
                    // the next sync attempt will not be silently green.
                    const message = validationErr instanceof Error ? validationErr.message : 'Token validation failed';
                    console.warn(`[DataSourceContext] ${initialSource.id}: token validation failed (non-auth), marking error`, validationErr);
                    const { itemCount, lastSync } = await loadSyncMeta();
                    return {
                      ...initialSource,
                      status: 'error' as DataSourceStatus,
                      error: message,
                      itemCount,
                      lastSync,
                    };
                  }
                }
              } catch {
                // BrowserTokenStorage read failed — fall through to LangGraph Store check
              }
              // No tokens in BrowserTokenStorage → fall through to LangGraph Store (backward compat)
            }

            // LangGraph Store path (Tauri, plaid, browser-extension, or PWA fallback)
            const tokenKey = `oauth_${initialSource.id}`;
            const tokenData = await store.get<StoredTokenData>(
              NS.uiPreferences(userId),
              tokenKey
            );

            if (tokenData?.accessToken) {
              const { itemCount, lastSync } = await loadSyncMeta();

              // Check if token is expired (5-min buffer)
              let isExpired = false;
              if (tokenData.expiresAt) {
                const expiresAtMs = typeof tokenData.expiresAt === 'string'
                  ? new Date(tokenData.expiresAt).getTime()
                  : tokenData.expiresAt;
                isExpired = expiresAtMs < Date.now() - 5 * 60 * 1000;
              }

              if (isExpired) {
                // Try to silently refresh if we have a refresh token
                if (tokenData.refreshToken && tokenData.provider !== 'plaid') {
                  try {
                    // Tauri: use Cloudflare Worker
                    const newTokens = await refreshOAuthToken(
                      tokenData.refreshToken,
                      tokenData.provider as 'google' | 'microsoft',
                    );
                    if (newTokens) {
                      await store.put(NS.uiPreferences(userId), tokenKey, {
                        accessToken: newTokens.accessToken,
                        refreshToken: newTokens.refreshToken || tokenData.refreshToken,
                        expiresAt: newTokens.expiresAt,
                        provider: tokenData.provider,
                        savedAt: Date.now(),
                      } as StoredTokenData);
                      await writeConnectedSourceAuthOutcome({
                        store,
                        userId,
                        sourceId: initialSource.id,
                      });
                      return {
                        ...initialSource,
                        status: 'connected' as DataSourceStatus,
                        itemCount,
                        lastSync,
                      };
                    }
                    return {
                      ...initialSource,
                      status: 'error' as DataSourceStatus,
                      error: 'Token refresh failed',
                      itemCount,
                      lastSync,
                    };
                  } catch (refreshErr) {
                    if (refreshErr instanceof ReAuthRequiredError) {
                      await writeNeedsReauthSourceAuthOutcome({
                        store,
                        userId,
                        sourceId: initialSource.id,
                        reason: 'refresh_expired',
                      });
                      await store.delete(NS.uiPreferences(userId), tokenKey);
                      return { ...initialSource, itemCount, lastSync };
                    }
                    console.warn(`[DataSourceContext] Token refresh failed for ${initialSource.id}:`, refreshErr);
                    const message = refreshErr instanceof Error ? refreshErr.message : 'Token refresh failed';
                    return {
                      ...initialSource,
                      status: 'error' as DataSourceStatus,
                      error: message,
                      itemCount,
                      lastSync,
                    };
                  }
                }
                if (!tokenData.refreshToken && tokenData.provider !== 'plaid') {
                  await writeNeedsReauthSourceAuthOutcome({
                    store,
                    userId,
                    sourceId: initialSource.id,
                    reason: 'missing_refresh_token',
                  });
                }
                return { ...initialSource, itemCount, lastSync };
              }

              await writeConnectedSourceAuthOutcome({
                store,
                userId,
                sourceId: initialSource.id,
              });
              return {
                ...initialSource,
                status: 'connected' as DataSourceStatus,
                itemCount,
                lastSync,
              };
            }
          } catch {
            // Token not found or error, keep disconnected
          }
          return initialSource;
        })
      );

      // RC2 (2026-05-19): use a functional update that preserves transient states
      // ('connecting', 'connected', 'error', 'syncing') set by concurrent
      // connectSource / syncSource / disconnectSource operations. The previous
      // `setDataSources(updatedSources)` clobbered those mid-operation states with
      // the from-disk snapshot, producing visible state regressions
      // (e.g. 'error' → 'disconnected' immediately after a failed connect).
      setDataSources(prev => {
        const updatedById = new Map(updatedSources.map(s => [s.id, s]));
        return prev.map(current => {
          const updated = updatedById.get(current.id);
          if (!updated) return current;
          // If a concurrent operation has put this source in a non-disconnected
          // state, leave it alone. Disconnected sources adopt the loaded state.
          if (current.status !== 'disconnected') return current;
          return updated;
        });
      });
    } catch (error) {
      console.error('[DataSourceContext] Failed to load saved tokens:', error);
    }
  };

  /**
   * Get a valid access token for a data source, refreshing if expired or forced
   * @param sourceId - The data source ID
   * @param forceRefresh - If true, refresh even if token appears valid (use after 401 errors)
   */
  const getValidToken = useCallback(async (sourceId: DataSourceId, forceRefresh = false): Promise<string | null> => {
    if (!store) return null;

    const tokenKey = `oauth_${sourceId}`;
    const storedTokenData = await store.get<StoredTokenData>(
      NS.uiPreferences(userId),
      tokenKey,
    );

    // PWA: delegate to oauth-integration for google/microsoft (reads BrowserTokenStorage, handles refresh)
    if (getPlatform() === 'pwa') {
      const source = INITIAL_DATA_SOURCES.find(s => s.id === sourceId);
      if (source?.provider === 'google') {
        try {
          const token = await getGoogleAccessToken();
          await writeConnectedSourceAuthOutcome({ store, userId, sourceId });
          return token;
        } catch (err) {
          if (err instanceof ReAuthRequiredError) {
            await writeNeedsReauthSourceAuthOutcome({
              store,
              userId,
              sourceId,
              reason: 'refresh_expired',
            });
            setDataSources(prev => prev.map(s =>
              s.id === sourceId ? { ...s, status: 'disconnected' as DataSourceStatus } : s
            ));
          }
          return null;
        }
      }
      if (source?.provider === 'microsoft') {
        try {
          const token = await getMicrosoftAccessToken();
          await writeConnectedSourceAuthOutcome({ store, userId, sourceId });
          return token;
        } catch (err) {
          if (err instanceof ReAuthRequiredError) {
            await writeNeedsReauthSourceAuthOutcome({
              store,
              userId,
              sourceId,
              reason: 'refresh_expired',
            });
            setDataSources(prev => prev.map(s =>
              s.id === sourceId ? { ...s, status: 'disconnected' as DataSourceStatus } : s
            ));
          }
          return null;
        }
      }
      // plaid, browser-extension: fall through to LangGraph Store logic
    }

    // LangGraph Store path (Tauri, plaid, browser-extension)
    const tokenData = storedTokenData;

    if (!tokenData?.accessToken) {
      console.error('[DataSourceContext] No token found for', sourceId);
      return null;
    }

    // Check if token is expired (with 5 minute buffer)
    const now = Date.now();
    const bufferMs = 5 * 60 * 1000;

    let expiresAtMs: number | undefined;
    if (tokenData.expiresAt) {
      expiresAtMs = typeof tokenData.expiresAt === 'string'
        ? new Date(tokenData.expiresAt).getTime()
        : tokenData.expiresAt;
    }

    const isExpired = expiresAtMs && (expiresAtMs - bufferMs) < now;
    const shouldRefresh = forceRefresh || isExpired;

    if (shouldRefresh && tokenData.refreshToken) {
      if (tokenData.provider === 'plaid') {
        return tokenData.accessToken;
      }

      // Tauri: use Cloudflare Worker
      let newTokens: OAuthTokenData | null;
      try {
        newTokens = await refreshOAuthToken(tokenData.refreshToken, tokenData.provider);
      } catch (err) {
        if (err instanceof ReAuthRequiredError) {
          await writeNeedsReauthSourceAuthOutcome({
            store,
            userId,
            sourceId,
            reason: 'refresh_expired',
          });
          await store.delete(NS.uiPreferences(userId), tokenKey);
          setDataSources(prev => prev.map(ds =>
            ds.id === sourceId ? {
              ...ds,
              status: 'disconnected' as DataSourceStatus,
              error: undefined,
              lastSync: undefined,
              itemCount: undefined,
            } : ds
          ));
          return null;
        }
        throw err;
      }

      if (newTokens) {
        const updatedTokenData: StoredTokenData = {
          accessToken: newTokens.accessToken,
          refreshToken: newTokens.refreshToken || tokenData.refreshToken,
          expiresAt: newTokens.expiresAt,
          provider: tokenData.provider,
          savedAt: Date.now(),
        };

        await store.put(NS.uiPreferences(userId), tokenKey, updatedTokenData);
        await writeConnectedSourceAuthOutcome({ store, userId, sourceId });
        console.log('[DataSourceContext] Token refreshed and stored successfully');
        return newTokens.accessToken;
      } else {
        console.error('[DataSourceContext] Token refresh failed');
        setDataSources(prev => prev.map(ds =>
          ds.id === sourceId ? {
            ...ds,
            status: 'error' as DataSourceStatus,
            error: 'Token refresh failed',
          } : ds
        ));
        return null;
      }
    }

    if (isExpired && !tokenData.refreshToken) {
      console.error('[DataSourceContext] Token expired and no refresh token - clearing invalid token');
      await writeNeedsReauthSourceAuthOutcome({
        store,
        userId,
        sourceId,
        reason: 'missing_refresh_token',
      });
      await store.delete(NS.uiPreferences(userId), tokenKey);
      setDataSources(prev => prev.map(ds =>
        ds.id === sourceId ? {
          ...ds,
          status: 'disconnected' as DataSourceStatus,
          error: undefined,
          lastSync: undefined,
          itemCount: undefined,
        } : ds
      ));
      return null;
    }

    await writeConnectedSourceAuthOutcome({ store, userId, sourceId });
    return tokenData.accessToken;
  }, [store, userId]);

  /**
   * Sync a data source - fetch data, classify, and store
   *
   * Email sync uses @ownyou/email package
   * Calendar/Financial sync are placeholders until packages are built
   *
   * NOTE: This function is defined BEFORE connectSource because connectSource
   * depends on it in its useCallback dependency array.
   */
  const syncSource = useCallback(async (sourceId: DataSourceId): Promise<SyncResult> => {
    console.log('[DataSourceContext] syncSource called for:', sourceId);
    if (!store || !isReady) {
      console.error('[DataSourceContext] Store not ready');
      throw new Error('Store not ready');
    }

    // Check if IAB classification is enabled (default OFF — only needed for BBS+ ad revenue)
    const iabEnabled = (() => {
      try {
        const saved = localStorage.getItem('ownyou_privacy_toggles');
        const migrated = localStorage.getItem('ownyou_privacy_iab_migrated');
        if (saved) {
          const parsed = JSON.parse(saved);
          // Migration: old default was true, new default is false. Route
          // through setDisclosurePreference so the same-tab credential-sync
          // listener picks up the change (raw localStorage.setItem only
          // notifies OTHER tabs/windows — see sixth-pass review M1).
          if (!migrated && parsed.iab === true) {
            parsed.iab = false;
            setDisclosurePreference('ownyou_privacy_toggles', JSON.stringify(parsed));
          }
          // Always set migration flag after evaluation (not just on mutation)
          if (!migrated) {
            localStorage.setItem('ownyou_privacy_iab_migrated', '1');
          }
          return parsed.iab === true;
        }
      } catch { /* ignore */ }
      // Fresh install: set flag so we don't re-evaluate every sync
      if (!localStorage.getItem('ownyou_privacy_iab_migrated')) {
        localStorage.setItem('ownyou_privacy_iab_migrated', '1');
      }
      return false; // default OFF
    })();

    const source = dataSources.find(ds => ds.id === sourceId);
    // Browsing sources bypass the disconnected check — the extension manages its own
    // auth state and auto-detect may call syncSource before React flushes the
    // setDataSources update (stale closure).
    if (!source) {
      console.log('[DataSourceContext] Source not found, skipping sync');
      recordMissionSpineMarker({
        runId: createMissionSpineRunId(`source-${sourceId}`),
        hop: 'source_write',
        status: 'skipped',
        namespace: 'unknown',
        sourceId,
        reasonCode: 'source-not-found',
        counts: { itemCount: 0 },
      });
      return { success: false, itemCount: 0, error: 'Source not found' };
    }
    if (source.status === 'disconnected' && source.type !== 'browsing') {
      console.log('[DataSourceContext] Source not connected, skipping sync');
      recordMissionSpineMarker({
        runId: createMissionSpineRunId(`source-${sourceId}`),
        hop: 'source_write',
        status: 'skipped',
        namespace: missionSpineNamespaceForSource(source.type),
        sourceId,
        reasonCode: 'source-not-connected',
        counts: { itemCount: 0 },
      });
      return { success: false, itemCount: 0, error: 'Source not connected' };
    }

    // Update status to syncing
    setDataSources(prev => prev.map(ds =>
      ds.id === sourceId ? { ...ds, status: 'syncing' as DataSourceStatus } : ds
    ));

    try {
      let accessToken: string | null = null;
      // Browsing source doesn't use OAuth tokens
      if (source.type !== 'browsing') {
        console.log('[DataSourceContext] Getting valid token for', sourceId);
        accessToken = await getValidToken(sourceId);

        if (!accessToken) {
          throw new Error('No valid access token available. Please reconnect.');
        }
        console.log('[DataSourceContext] Valid access token obtained, length:', accessToken.length);
      }

      let itemCount = 0;
      if (source.type === 'email') {
        // ========================================
        // EMAIL SYNC - Uses @ownyou/email package
        // Sprint 11b Deviation 1 Fix: Read fetchOptions from Store
        // ========================================
        console.log('[DataSourceContext] Starting email sync for', sourceId);
        try {
          console.log('[DataSourceContext] Importing @ownyou/email...');
          const { EmailPipeline } = await import('@ownyou/email');
          console.log('[DataSourceContext] Importing @ownyou/iab-classifier...');
          const { createIABClassifier, RouteBackedAnalyzerLLMClient } = await import('@ownyou/iab-classifier');
          console.log('[DataSourceContext] Imports successful');

          // Create IAB classifier from saved V2 route selections or executable
          // OwnYou route defaults. Normal sync never prompts users to configure
          // the full classifier suite; unavailable plans defer before prompt send.
          const routeProxyToken = iabEnabled ? await getAccessToken() : null;
          const iabRoutePlan = iabEnabled ? buildIabRoutePlan(routeProxyToken) : null;
          const iabClassifier = iabRoutePlan
            ? createIABClassifier({
                store: store as unknown as Parameters<typeof createIABClassifier>[0]['store'],
                llm_route_client_factory: ({ routeId, fallbackRouteId }) => {
                  const resolved = iabRoutePlan.routes[routeId as ModelRouteId]
                    ?? (fallbackRouteId ? iabRoutePlan.routes[fallbackRouteId as ModelRouteId] : null);
                  if (!resolved) {
                    throw new Error(`unavailable_route:${routeId}`);
                  }

                  return new RouteBackedAnalyzerLLMClient({
                    routeId,
                    provider: resolved.provider,
                    model: resolved.model,
                    executeRoute: async ({ routeId: executionRouteId, messages, temperature, maxTokens }) => {
                      const plannedExecutionSelection = iabRoutePlan.routes[executionRouteId as ModelRouteId];
                      if (!plannedExecutionSelection) {
                        console.debug('[DataSourceContext] IAB route plan missed execution route; using factory fallback selection', {
                          routeId: executionRouteId,
                          fallbackRouteId: routeId,
                        });
                      }
                      const executionSelection = plannedExecutionSelection ?? resolved;
                      const routeResult = await executeLlmTextRoute({
                        routeId: executionRouteId,
                        selection: {
                          provider: executionSelection.provider,
                          model: executionSelection.model,
                        },
                        selectionSource: executionSelection.selectionSource,
                        messages,
                        temperature,
                        maxTokens,
                        authToken: routeProxyToken,
                        emitTelemetry: (event) => {
                          console.info('[DataSourceContext] IAB route telemetry', {
                            routeId: event.routeId,
                            provider: event.provider,
                            model: event.model,
                            selectionSource: event.selectionSource,
                            result: event.result,
                            errorCode: event.errorCode,
                            validation: event.validation,
                            latencyMs: event.latencyMs,
                            costKnown: event.costKnown,
                          });
                        },
                      });

                      return {
                        content: routeResult.text,
                        provider: routeResult.provider,
                        model: routeResult.model,
                        usage: routeResult.usage,
                      };
                    },
                  });
                },
              })
            : null;

          // Fetch user-configurable fetchOptions from Store (Sprint 11b Deviation 1 fix)
          // Falls back to defaults if not configured
          const DEFAULT_FETCH_OPTIONS = { maxResults: 100, daysBack: 30 };
          let userFetchOptions = DEFAULT_FETCH_OPTIONS;
          try {
            const configData = await store.get<{ fetchOptions?: { maxResults?: number; daysBack?: number } }>(
              NS.dataSourceConfigs(userId),
              sourceId
            );
            if (configData?.fetchOptions) {
              userFetchOptions = { ...DEFAULT_FETCH_OPTIONS, ...configData.fetchOptions };
              console.log('[DataSourceContext] Using user-configured fetchOptions:', userFetchOptions);
            }
          } catch (configError) {
            console.log('[DataSourceContext] No user fetchOptions config, using defaults');
          }

          // Create email pipeline with PipelineConfig (userId is required)
          console.log('[DataSourceContext] Creating EmailPipeline for provider:', source.provider);
          // Always set runClassification=true so the callback is invoked and emails
          // are stored to the Store for the ikigai pipeline. The callback itself
          // decides whether to run IAB classification based on iabEnabled.
          const pipeline = new EmailPipeline({
            userId,
            provider: source.provider as 'google' | 'microsoft',
            runClassification: true,
            fetchOptions: {
              maxResults: userFetchOptions.maxResults,
            },
          });

          // Run pipeline with classifier callback
          console.log('[DataSourceContext] Running email pipeline...');
          // The callback receives emails and must return IABClassification[] (@ownyou/email type)
          // We map from @ownyou/iab-classifier output to @ownyou/email IABClassification format
          const result = await pipeline.run(accessToken!, async (emails) => {
            const results: Array<{
              emailId: string;
              tier1Category: string;
              tier1Id: string;
              tier2Category?: string;
              tier2Id?: string;
              confidence: number;
              reasoning?: string;
              classifiedAt: Date;
            }> = [];

            // CRITICAL FIX: Store raw email data for RawData.tsx to display
            // Store each email's summary to NS.iabClassifications namespace
            console.log(`[DataSourceContext] Storing ${emails.length} emails to Store...`);
            for (const email of emails) {
              try {
                // Store email summary for the RawData viewer
                await store.put(
                  NS.iabClassifications(userId),
                  `email_${email.id}`,
                  {
                    id: email.id,
                    summary: email.subject || 'No subject',
                    content: email.body?.substring(0, 500) || '',
                    sourceType: 'email',
                    from: email.from,
                    to: email.to,
                    date: email.date instanceof Date ? email.date.toISOString() : email.date,
                    timestamp: new Date().toISOString(),
                    metadata: {
                      provider: source.provider,
                      hasAttachments: email.metadata?.hasAttachments || false,
                    },
                  }
                );
              } catch (storeErr) {
                console.warn('[DataSourceContext] Failed to store one email:', storeErr);
              }
            }
            console.log('[DataSourceContext] Emails stored to namespace');

            // Skip classification if IAB is disabled
            if (!iabClassifier) {
              return results;
            }

            // Helper to process a single email
            const processEmail = async (email: any) => {
              try {
                const classResult = await iabClassifier!.invoke({
                  userId,
                  source: 'email',
                  sourceItemId: email.id,
                  text: `${email.subject} ${email.body ?? ''}`,
                });
                if (classResult.success && classResult.classification) {
                  // Slice 0 (ADR 0004): no enum→tier-1 dumb-down on this path.
                  // The reconciler writes the canonical structured per-section
                  // records (with numeric `taxonomy_id`) into
                  // `NS.iabClassifications` directly; the wire-builder reads
                  // those records, not this return shape. The fields below
                  // satisfy `@ownyou/email`'s IABClassification type contract
                  // for pipeline counting; `tier1Id` is no longer derived.
                  const c = classResult.classification;
                  return {
                    emailId: email.id,
                    tier1Category: typeof c.category === 'string' ? c.category : 'Unknown',
                    tier1Id: '',
                    tier2Category: undefined,
                    tier2Id: undefined,
                    confidence: typeof c.confidence === 'number' ? c.confidence : 0.5,
                    reasoning: c.reasoning,
                    classifiedAt: new Date(),
                  };
                }
              } catch (err) {
                console.warn('[DataSourceContext] Classification failed for email:', err);
              }
              return null;
            };

            // Process emails in batches of 5 to avoid rate limits but improve speed
            const BATCH_SIZE = 5;
            for (let i = 0; i < emails.length; i += BATCH_SIZE) {
              const batch = emails.slice(i, i + BATCH_SIZE);
              console.log(`[DataSourceContext] Processing batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(emails.length / BATCH_SIZE)} (${batch.length} emails)`);

              const batchResults = await Promise.all(batch.map(processEmail));

              // Filter out nulls (failed classifications) and add to results
              batchResults.forEach(res => {
                if (res) results.push(res);
              });
            }

            return results;
          });

          // PipelineResult has emailsFetched, not emailCount
          console.log('[DataSourceContext] Pipeline completed:', result);
          itemCount = result.emailsFetched ?? 0;
          console.log('[DataSourceContext] Emails fetched:', itemCount);
        } catch (err) {
          console.error('[DataSourceContext] Email sync failed:', err);
          throw err;
        }

      } else if (source.type === 'calendar') {
        // ========================================
        // CALENDAR SYNC — Uses @ownyou/data-calendar
        // Sprint 15: Fetch → Classify → Store pipeline
        // ========================================
        console.log('[DataSourceContext] Starting calendar sync for', sourceId);
        try {
          const {
            createCalendarFetcher,
            createCalendarClassifier,
            createCalendarStore,
          } = await import('@ownyou/data-calendar');

          // Create pipeline components
          const fetcher = createCalendarFetcher({
            useMock: false,
            provider: source.provider === 'microsoft' ? 'microsoft' : 'google',
          });
          const calendarStore = createCalendarStore(store as any);

          // 1. Fetch all events
          const { events, syncToken } = await fetcher.fetchAllEvents(accessToken!, userId);
          console.log(`[DataSourceContext] Fetched ${events.length} calendar events`);

          // 2. Classify events with IAB categories (only if enabled)
          let eventsToStore = events;
          let classifiedCount = 0;
          if (iabEnabled) {
            const classifier = createCalendarClassifier({ confidenceThreshold: 0.5, modelTier: 'fast' });
            eventsToStore = await classifier.classifyBatch(events, userId);
            classifiedCount = eventsToStore.filter((e: any) => e.iabClassification).length;
          }


          // 3. Save events and build profile
          await calendarStore.saveEvents(userId, eventsToStore);
          await calendarStore.saveProfile(userId, eventsToStore);

          // 4. Record sync result
          await calendarStore.recordSync(userId, {
            success: true,
            newEvents: events.length,
            updatedEvents: 0,
            removedEvents: 0,
            classifiedCount,
            syncedAt: Date.now(),
            nextSyncToken: syncToken,
          });

          itemCount = events.length;
          console.log(`[DataSourceContext] Calendar sync complete: ${itemCount} events`);
        } catch (err) {
          console.error('[DataSourceContext] Calendar sync failed:', err);
          throw err;
        }

      } else if (source.type === 'financial') {
        // ========================================
        // FINANCIAL SYNC — Uses @ownyou/data-financial
        // Sprint 15: Fetch → Classify → Store pipeline
        // ========================================
        console.log('[DataSourceContext] Starting financial sync for', sourceId);
        try {
          const {
            createTransactionFetcher,
            createTransactionClassifier,
            createFinancialStore,
          } = await import('@ownyou/data-financial');

          // Determine whether to use real Plaid or mock
          const { isApiConfigured, apiKeys } = await import('../config/api-keys');
          const { loadPublicRuntimeConfig } = await import('../config/runtime-public-config');
          const { plaidEdgeFunctionUrl } = await loadPublicRuntimeConfig();
          const plaidEdgeUrl = apiKeys.plaidEdgeUrl || plaidEdgeFunctionUrl;
          const useRealPlaid = isApiConfigured('plaidEdgeUrl') || Boolean(plaidEdgeUrl);
          const plaidProxyToken = useRealPlaid ? await getAccessToken() : null;
          if (useRealPlaid && !plaidProxyToken) {
            throw new Error('Sign in required to sync Plaid');
          }

          // Create pipeline components
          const fetcher = createTransactionFetcher({
            useMock: !useRealPlaid,
            edgeFunctionUrl: useRealPlaid ? plaidEdgeUrl : undefined,
            authToken: plaidProxyToken ?? undefined,
          });
          const financialStore = createFinancialStore(store as any);

          // 1. Fetch all transactions
          const { transactions } = await fetcher.fetchAllTransactions(accessToken!, userId);
          console.log(`[DataSourceContext] Fetched ${transactions.length} transactions`);

          // 2. Classify transactions with IAB categories (only if enabled)
          let txnsToStore = transactions;
          let classifiedCount = 0;
          if (iabEnabled) {
            const classifier = createTransactionClassifier({ confidenceThreshold: 0.7, modelTier: 'fast' });
            txnsToStore = await classifier.classifyBatch(transactions, userId);
            classifiedCount = txnsToStore.filter((t: any) => t.iabClassification).length;
          }


          // 3. Save transactions and build profile
          await financialStore.saveTransactions(userId, txnsToStore);
          await financialStore.saveProfile(userId, txnsToStore);

          // 4. Record sync result
          await financialStore.recordSync(userId, {
            success: true,
            newTransactions: transactions.length,
            updatedTransactions: 0,
            removedTransactions: 0,
            classifiedCount,
            syncedAt: Date.now(),
          });

          itemCount = transactions.length;
          console.log(`[DataSourceContext] Financial sync complete: ${itemCount} transactions`);
        } catch (err) {
          console.error('[DataSourceContext] Financial sync failed:', err);
          throw err;
        }

      } else if (source.type === 'browsing') {
        // ========================================
        // BROWSING SYNC — Uses OwnYouBrowsing bridge API
        // Reads browsing history from extension → stores in Store namespace
        // ========================================
        console.log('[DataSourceContext] Starting browsing sync for', sourceId);
        try {
          const browsingApi = (window as any).OwnYouBrowsing;
          if (!browsingApi || !browsingApi.isAvailable()) {
            throw new Error('OwnYou browser extension not detected. Please install and enable the extension.');
          }

          const hasPermission = await browsingApi.hasPermission();
          if (!hasPermission) {
            throw new Error('Browsing history permission not granted. Open the extension popup to enable it.');
          }

          // Fetch browsing entries from extension (last 30 days, up to 500)
          const result = await browsingApi.getHistory({ days: 30, maxResults: 500 });
          const entries = result.entries || [];
          console.log(`[DataSourceContext] Fetched ${entries.length} browsing entries from extension`);

          if (entries.length > 0) {
            // Store each entry in the browsing namespace
            const browsingNs = NS.browsingHistory(userId);
            for (const entry of entries) {
              await store.put(browsingNs, `browse_${entry.hash || entry.domain + '_' + Date.now()}`, {
                url: entry.url,
                title: entry.title,
                domain: entry.domain,
                visitCount: entry.visitCount || 1,
                duration: entry.duration || 0,
                lastVisitTime: entry.lastVisitTime,
                transition: entry.transition || 'unknown',
                sourceType: 'browsing',
              });
            }
            console.log(`[DataSourceContext] Stored ${entries.length} browsing entries to Store`);
          }

          itemCount = entries.length;
          console.log(`[DataSourceContext] Browsing sync complete: ${itemCount} entries`);
        } catch (err) {
          console.error('[DataSourceContext] Browsing sync failed:', err);
          throw err;
        }
      }

      // Update to connected with sync info
      const syncTime = new Date();
      const missionSpineRunId = createMissionSpineRunId(`source-${sourceId}`);
      setDataSources(prev => prev.map(ds =>
        ds.id === sourceId ? {
          ...ds,
          status: 'connected' as DataSourceStatus,
          lastSync: syncTime,
          itemCount,
          error: undefined,
        } : ds
      ));

      // BUG 1 FIX: Persist sync metadata to Store so it survives reloads
      try {
        const syncMeta: StoredSyncMetadata = {
          itemCount,
          lastSync: syncTime.toISOString(),
        };
        await store.put(NS.uiPreferences(userId), `sync_${sourceId}`, syncMeta);
        console.log(`[DataSourceContext] Persisted sync metadata for ${sourceId}:`, syncMeta);
      } catch (persistErr) {
        console.warn('[DataSourceContext] Failed to persist sync metadata:', persistErr);
      }

      // Notify agent worker that new data is available in IndexedDB.
      // The worker's StoreWatcher only detects writes from its own MemoryStore
      // instance, so we bridge the gap with a CustomEvent.
      try {
        recordMissionSpineMarker({
          runId: missionSpineRunId,
          hop: 'source_write',
          status: 'success',
          namespace: missionSpineNamespaceForSource(source.type),
          sourceId,
          reasonCode: `${source.type}-sync-complete`,
          counts: { itemCount },
        });

        window.dispatchEvent(new CustomEvent('ownyou:data-changed', {
          detail: { namespace: 'iab_classifications', sourceId, runId: missionSpineRunId },
        }));
        console.log(`[DataSourceContext] Dispatched data-changed event for ${sourceId}`);
      } catch {
        // Non-critical — worker will still pick up data on next scheduled trigger
      }

      // Sprint 16b: Notify entity detection pipeline that new data is available.
      // Maps DataSourceType → LoveSource for the auto-trigger coalescing window.
      const loveSourceMap: Record<string, string> = {
        email: 'email',
        calendar: 'calendar',
        financial: 'financial',
        browsing: 'browsing',
      };
      const loveSource = loveSourceMap[source.type];
      if (loveSource) {
        try {
          window.dispatchEvent(new CustomEvent('data-changed', {
            detail: { source: loveSource },
          }));
          console.log(`[DataSourceContext] Dispatched data-changed for entity pipeline: ${loveSource}`);
        } catch {
          // Non-critical
        }
      }

      return { success: true, itemCount };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sync failed';
      recordMissionSpineMarker({
        runId: createMissionSpineRunId(`source-${sourceId}`),
        hop: 'source_write',
        status: 'error',
        namespace: missionSpineNamespaceForSource(source.type),
        sourceId,
        reasonCode: 'source-sync-failed',
        counts: { itemCount: 0 },
      });

      setDataSources(prev => prev.map(ds =>
        ds.id === sourceId ? {
          ...ds,
          status: 'error' as DataSourceStatus,
          error: errorMessage,
        } : ds
      ));

      return { success: false, itemCount: 0, error: errorMessage };
    }
  }, [store, isReady, userId, dataSources, buildIabRoutePlan, getAccessToken]);

  /**
   * Connect a data source with OAuth token data (including refresh token)
   */
  const connectSource = useCallback(async (sourceId: DataSourceId, tokenData: OAuthTokenData) => {
    if (!store || !isReady) throw new Error('Store not ready');

    // Update status to connecting
    setDataSources(prev => prev.map(ds =>
      ds.id === sourceId ? { ...ds, status: 'connecting' as DataSourceStatus } : ds
    ));

    try {
      // Store the full token data securely (including refresh token)
      const storedData: StoredTokenData = {
        accessToken: tokenData.accessToken,
        refreshToken: tokenData.refreshToken,
        expiresAt: tokenData.expiresAt,
        provider: tokenData.provider,
        savedAt: Date.now(),
      };

      // RC2 (2026-05-19): validate BEFORE committing to LangGraph Store.
      // Previously the LangGraph Store write happened first, so a concurrent
      // loadSavedTokens could read the not-yet-validated token and overwrite
      // the validation-failure state with 'connected'. We now stage the token
      // only in BrowserTokenStorage (which the validator reads from), validate,
      // then commit to LangGraph Store only on success.
      //
      // Stage in BrowserTokenStorage so the PWA access-token getter can find
      // the supplied refresh token during validation.
      if (tokenData.refreshToken && tokenData.provider !== 'plaid') {
        try {
          const tokenManager = getTokenManager();
          await tokenManager.storeTokens('default_user', tokenData.provider as 'google' | 'microsoft', {
            accessToken: tokenData.accessToken,
            refreshToken: tokenData.refreshToken,
            expiresAt: tokenData.expiresAt ?? Date.now() + 3600000,
            scope: '',
            tokenType: 'Bearer',
          });
        } catch {
          // Non-critical — BrowserTokenStorage sync is best-effort
        }
      }

      // Validate by exercising the access-token getter (handles refresh).
      if (getPlatform() === 'pwa' && (tokenData.provider === 'google' || tokenData.provider === 'microsoft')) {
        try {
          if (tokenData.provider === 'google') {
            await getGoogleAccessToken();
          } else {
            await getMicrosoftAccessToken();
          }
        } catch (validationErr) {
          // Rollback BrowserTokenStorage; LangGraph Store was never written.
          try {
            const tokenManager = getTokenManager();
            await tokenManager.deleteTokens('default_user', tokenData.provider as 'google' | 'microsoft');
          } catch {
            // best-effort rollback
          }
          const message = validationErr instanceof Error ? validationErr.message : 'Token validation failed';
          throw new Error(`[connectSource] ${sourceId} token validation failed: ${message}`);
        }
      }

      // Validation passed — commit to LangGraph Store.
      await store.put(NS.uiPreferences(userId), `oauth_${sourceId}`, storedData);
      await writeConnectedSourceAuthOutcome({ store, userId, sourceId });

      console.log('[DataSourceContext] Token stored:', {
        sourceId,
        hasRefreshToken: !!tokenData.refreshToken,
        expiresAt: tokenData.expiresAt ? new Date(tokenData.expiresAt).toISOString() : 'unknown',
        provider: tokenData.provider,
      });

      // Update to connected
      setDataSources(prev => prev.map(ds =>
        ds.id === sourceId ? { ...ds, status: 'connected' as DataSourceStatus } : ds
      ));

      // Auto-connect sibling sources sharing the same provider
      // (e.g., connecting Outlook also connects Microsoft Calendar)
      const siblings = INITIAL_DATA_SOURCES.filter(
        s => s.provider === tokenData.provider && s.id !== sourceId && tokenData.provider !== 'plaid'
      );
      for (const sibling of siblings) {
        try {
          await store.put(NS.uiPreferences(userId), `oauth_${sibling.id}`, storedData);
          await writeConnectedSourceAuthOutcome({ store, userId, sourceId: sibling.id });
          setDataSources(prev => prev.map(s =>
            s.id === sibling.id ? { ...s, status: 'connected' as DataSourceStatus } : s
          ));
        } catch {
          // Non-critical — sibling auto-connect is best-effort
        }
      }

      console.log(`[DataSourceContext] ${sourceId} connected. User can manually sync when ready.`);
    } catch (error) {
      setDataSources(prev => prev.map(ds =>
        ds.id === sourceId ? {
          ...ds,
          status: 'error' as DataSourceStatus,
          error: error instanceof Error ? error.message : 'Connection failed'
        } : ds
      ));
      throw error;
    }
  }, [store, isReady, userId]); // BUG 3 FIX: Removed syncSource from deps (no longer called)

  /**
   * Disconnect a data source
   */
  const disconnectSource = useCallback(async (sourceId: DataSourceId) => {
    if (!store || !isReady) throw new Error('Store not ready');

    try {
      // Remove the saved token
      await store.delete(NS.uiPreferences(userId), `oauth_${sourceId}`);
      await writeDisconnectedSourceAuthOutcome({ store, userId, sourceId });

      // Update to disconnected
      setDataSources(prev => prev.map(ds =>
        ds.id === sourceId ? {
          ...ds,
          status: 'disconnected' as DataSourceStatus,
          lastSync: undefined,
          itemCount: undefined,
          error: undefined,
        } : ds
      ));
    } catch (error) {
      console.error('[DataSourceContext] Failed to disconnect:', error);
      throw error;
    }
  }, [store, isReady, userId]);

  /**
   * Attempt to refresh expired tokens for disconnected sources.
   * Returns IDs of sources that HAD stored tokens but could NOT be refreshed
   * (i.e. need re-auth). Sources that were never connected are excluded.
   */
  const refreshExpiredTokens = useCallback(async (): Promise<DataSourceId[]> => {
    if (!store || !isReady) return [];

    const disconnected = dataSources.filter(ds => ds.status === 'disconnected' && ds.type !== 'browsing');
    const needsReauth: DataSourceId[] = [];

    for (const ds of disconnected) {
      try {
        if (getPlatform() === 'pwa' && (ds.provider === 'google' || ds.provider === 'microsoft')) {
          // PWA: delegate to oauth-integration (reads BrowserTokenStorage, handles refresh)
          try {
            if (ds.provider === 'microsoft') {
              await getMicrosoftAccessToken();
            } else {
              await getGoogleAccessToken();
            }
            await writeConnectedSourceAuthOutcome({ store, userId, sourceId: ds.id });
            setDataSources(prev => prev.map(s =>
              s.id === ds.id ? { ...s, status: 'connected' as DataSourceStatus } : s
            ));
            continue;
          } catch (err) {
            if (err instanceof ReAuthRequiredError) {
              await writeNeedsReauthSourceAuthOutcome({
                store,
                userId,
                sourceId: ds.id,
                reason: 'refresh_expired',
              });
              needsReauth.push(ds.id);
              continue;
            }
            throw err;
          }
        }

        // Tauri / plaid: use LangGraph Store + Cloudflare Worker
        const tokenKey = `oauth_${ds.id}`;
        const tokenData = await store.get<StoredTokenData>(NS.uiPreferences(userId), tokenKey);

        if (!tokenData?.refreshToken || tokenData.provider === 'plaid') {
          if (tokenData?.accessToken && tokenData.provider !== 'plaid') {
            await writeNeedsReauthSourceAuthOutcome({
              store,
              userId,
              sourceId: ds.id,
              reason: 'missing_refresh_token',
            });
            needsReauth.push(ds.id);
          }
          continue;
        }

        const newTokens = await refreshOAuthToken(
          tokenData.refreshToken,
          tokenData.provider as 'google' | 'microsoft',
        );

        if (newTokens) {
          await store.put(NS.uiPreferences(userId), tokenKey, {
            accessToken: newTokens.accessToken,
            refreshToken: newTokens.refreshToken || tokenData.refreshToken,
            expiresAt: newTokens.expiresAt,
            provider: tokenData.provider,
            savedAt: Date.now(),
          } as StoredTokenData);
          await writeConnectedSourceAuthOutcome({ store, userId, sourceId: ds.id });

          setDataSources(prev => prev.map(s =>
            s.id === ds.id ? { ...s, status: 'connected' as DataSourceStatus } : s
          ));
          continue;
        }

        setDataSources(prev => prev.map(s =>
          s.id === ds.id ? { ...s, status: 'error' as DataSourceStatus, error: 'Token refresh failed' } : s
        ));
        continue;
      } catch (err) {
        console.warn(`[DataSourceContext] Token refresh failed for ${ds.id}:`, err);
        if (err instanceof ReAuthRequiredError) {
          await writeNeedsReauthSourceAuthOutcome({
            store,
            userId,
            sourceId: ds.id,
            reason: 'refresh_expired',
          });
          needsReauth.push(ds.id);
          continue;
        }
        setDataSources(prev => prev.map(s =>
          s.id === ds.id ? {
            ...s,
            status: 'error' as DataSourceStatus,
            error: err instanceof Error ? err.message : 'Token refresh failed',
          } : s
        ));
      }
    }

    return needsReauth;
  }, [store, isReady, userId, dataSources]);

  /**
   * Sync all connected data sources
   */
  const syncAll = useCallback(async () => {
    setIsSyncing(true);
    try {
      const connected = dataSources.filter(ds => ds.status === 'connected');
      await Promise.all(connected.map(ds => syncSource(ds.id)));
    } finally {
      setIsSyncing(false);
    }
  }, [dataSources, syncSource]);

  /**
   * Get all connected sources
   */
  const getConnectedSources = useCallback(() => {
    return dataSources.filter(ds => ds.status === 'connected');
  }, [dataSources]);

  /**
   * Check if a specific source is connected
   */
  const isSourceConnected = useCallback((sourceId: DataSourceId): boolean => {
    const source = dataSources.find(ds => ds.id === sourceId);
    return source?.status === 'connected';
  }, [dataSources]);

  const value: DataSourceContextValue = {
    dataSources,
    connectSource,
    disconnectSource,
    syncSource,
    syncAll,
    isSyncing,
    getConnectedSources,
    isSourceConnected,
    getValidToken,
    refreshExpiredTokens,
  };

  return (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  );
}

export function useDataSource() {
  const context = useContext(DataSourceContext);
  if (!context) {
    throw new Error('useDataSource must be used within a DataSourceProvider');
  }
  return context;
}

/**
 * Hook for managing a specific data source
 */
export function useDataSourceConnection(sourceId: DataSourceId) {
  const { dataSources, connectSource, disconnectSource, syncSource, isSourceConnected } = useDataSource();

  const source = dataSources.find(ds => ds.id === sourceId);

  const connect = useCallback(async (tokenData: OAuthTokenData) => {
    await connectSource(sourceId, tokenData);
  }, [sourceId, connectSource]);

  const disconnect = useCallback(async () => {
    await disconnectSource(sourceId);
  }, [sourceId, disconnectSource]);

  const sync = useCallback(async () => {
    return syncSource(sourceId);
  }, [sourceId, syncSource]);

  return {
    source,
    isConnected: isSourceConnected(sourceId),
    connect,
    disconnect,
    sync,
  };
}
